import { css } from '@emotion/react';
import type { FC } from 'react';
import { guaranteeHash } from '~/_shared/components/colorPicker/colorPicker.helpers';
import { LimitNumberOfLinesComponent } from '~/_shared/components/limitNumberOfLines/limitNumberOfLines.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { defaultLocationDataStyle } from '~/customizeLocationPanel/customizeLocationPanel.constants';

// mirrors the read-only rendering of BubbleItemComponent so place details match the location panel
const textCommonStyle = ({ fontColor, fontSize }: { fontColor: string; fontSize: string }) => css({
  color: guaranteeHash(fontColor),
  fontSize: `${fontSize}px`,
  textDecoration: 'none',
});

const ellipsisStyle = css({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const valueRowStyle = css({
  alignItems: 'center',
  display: 'flex',
  flex: 1,
  minWidth: 0,
});

const valueContentStyle = css({
  flex: 1,
  minWidth: 0,
});

const labelStyle = css({ textTransform: 'uppercase' });

export type PlaceDetailItemProps = Readonly<{
  label: string;
  value: string;
  href?: string;
}>;

export const PlaceDetailItemComponent: FC<PlaceDetailItemProps> = ({ label, value, href }) => {
  const theme = useTheme();
  const { labelFontColor, labelFontSize, valueFontColor, valueFontSize } = defaultLocationDataStyle(theme);

  const valueFontProps = { fontColor: valueFontColor, fontSize: valueFontSize };

  return (
    <div css={textCommonStyle({ fontColor: labelFontColor, fontSize: labelFontSize })}>
      <div css={labelStyle}>{label}</div>

      <div css={valueRowStyle}>
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            css={[ellipsisStyle, textCommonStyle(valueFontProps), valueContentStyle, { display: 'block' }]}
          >
            {value}
          </a>
        ) : (
          <LimitNumberOfLinesComponent
            maxNumberOfLines={1000}
            css={[textCommonStyle(valueFontProps), valueContentStyle]}
          >
            {value}
          </LimitNumberOfLinesComponent>
        )}
      </div>
    </div>
  );
};
